import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Lightbulb, HelpCircle } from "lucide-react";
import { getRoleFromToken } from "../../api";
import { getModuloDocumentacao } from "./content";
import { getIconeModulo } from "./icones";

function ConteudoSubModulo({ item }) {
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <p className="text-sm text-gray-500 max-w-xl">{item.descricao}</p>
        {item.pathModulo && (
          <button
            onClick={() => navigate(item.pathModulo)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:shadow-sm transition-shadow whitespace-nowrap"
          >
            Ir para o módulo <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>

      {item.passoAPasso?.length > 0 && (
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">Passo a passo</h2>
          <ol className="space-y-4">
            {item.passoAPasso.map((passo, idx) => (
              <li key={idx} className="flex gap-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{passo.titulo}</p>
                  <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{passo.texto}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {item.dicas?.length > 0 && (
        <section className="bg-purple-50 rounded-2xl border border-purple-100 p-6 mb-6">
          <h2 className="flex items-center gap-2 text-sm font-bold text-purple-800 uppercase tracking-wide mb-4">
            <Lightbulb className="w-4 h-4" /> Dicas e boas práticas
          </h2>
          <ul className="space-y-2">
            {item.dicas.map((dica, idx) => (
              <li key={idx} className="flex gap-2 text-sm text-purple-900">
                <span className="text-purple-400">•</span>
                <span>{dica}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {item.faq?.length > 0 && (
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">Perguntas frequentes</h2>
          <div className="space-y-4">
            {item.faq.map((faqItem, idx) => (
              <details key={idx} className="group border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                <summary className="cursor-pointer text-sm font-semibold text-gray-800 flex justify-between items-center">
                  {faqItem.pergunta}
                  <span className="text-gray-400 group-open:rotate-180 transition-transform">⌄</span>
                </summary>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">{faqItem.resposta}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function ModuloDocumentacao() {
  const navigate = useNavigate();
  const { moduloId } = useParams();
  const userRole = getRoleFromToken();
  const modulo = getModuloDocumentacao(moduloId);

  const acessoPermitido = modulo && (!modulo.allowedRoles || modulo.allowedRoles.includes(userRole));

  const subModulosVisiveis = useMemo(() => {
    if (!modulo?.subModulos) return null;
    return modulo.subModulos.filter((m) => !m.allowedRoles || m.allowedRoles.includes(userRole));
  }, [modulo, userRole]);

  const [subModuloAtivoId, setSubModuloAtivoId] = useState(null);

  useEffect(() => {
    if (subModulosVisiveis?.length) {
      setSubModuloAtivoId((atual) =>
        subModulosVisiveis.some((m) => m.id === atual) ? atual : subModulosVisiveis[0].id
      );
    }
  }, [subModulosVisiveis]);

  useEffect(() => {
    if (!acessoPermitido || (modulo?.subModulos && subModulosVisiveis?.length === 0)) {
      navigate("/central-ajuda", { replace: true });
    }
  }, [acessoPermitido, modulo, subModulosVisiveis, navigate]);

  if (!acessoPermitido) return null;
  if (modulo.subModulos && subModulosVisiveis?.length === 0) return null;

  const Icon = getIconeModulo(modulo.icone);
  const subModuloAtivo = subModulosVisiveis?.find((m) => m.id === subModuloAtivoId) || subModulosVisiveis?.[0];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/central-ajuda")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <HelpCircle className="w-5 h-5" />
          <span className="text-lg font-bold tracking-tight">Central de Ajuda</span>
        </div>
        <button
          onClick={() => navigate("/inicio")}
          className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
        >
          Menu Principal
        </button>
      </header>

      <main className="flex-1 px-6 py-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-br from-purple-900 to-pink-800 text-white p-3 rounded-xl shadow-md">
              <Icon className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{modulo.titulo}</h1>
              {subModulosVisiveis && (
                <p className="text-sm text-gray-500 mt-1 max-w-xl">{modulo.descricao}</p>
              )}
            </div>
          </div>

          {subModulosVisiveis ? (
            <>
              <div className="flex gap-2 mb-6 flex-wrap bg-white rounded-xl border border-gray-100 shadow-sm p-1.5 w-fit">
                {subModulosVisiveis.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSubModuloAtivoId(m.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      subModuloAtivo?.id === m.id
                        ? "bg-gradient-to-r from-purple-900 to-pink-800 text-white"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {m.titulo}
                  </button>
                ))}
              </div>
              {subModuloAtivo && <ConteudoSubModulo item={subModuloAtivo} />}
            </>
          ) : (
            <ConteudoSubModulo item={modulo} />
          )}
        </div>
      </main>
    </div>
  );
}
